// 263: Úkoly ke schválení na úvodu PC (snímek „Směna a ke schválení“) — vedle docházky.
// Hotové úkoly (stav Hotovo) čekají na vedoucího. Úkol s fotkami se schvaluje až po prohlédnutí:
// zelené tlačítko ho otevře; úkol bez fotek schválí rovnou.
// 356: úkol se otevře v panelu zprava přímo na Přehledu (dřív přechod na stránku úkolu) —
// ↑ ↓ / najetí myší vybere, mezerník otevře, v panelu ↑ ↓ další úkol fronty.
import { useCallback, useEffect, useState } from "react";
import { Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import TaskDetailModal from "@/components/TaskDetailModal";
import { useListNav, Kbd } from "@/components/shared/ListNav";

const word = (n) => (n === 1 ? "úkol" : n >= 2 && n <= 4 ? "úkoly" : "úkolů");

export function TaskApprovalBand({ nav, onCount, reloadKey, keys = false }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [navIds, setNavIds] = useState([]);   // pořadí z chvíle otevření — schválený úkol z fronty zmizí, listovat jde dál
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks?status=open");
      const list = (Array.isArray(data) ? data : data?.tasks || []).filter((t) => t.status === "Hotovo")
        .sort((a, b) => String(b.completion?.at || "").localeCompare(String(a.completion?.at || "")));
      setRows(list);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => { if (rows) onCount?.(rows.length); }, [rows, onCount]);

  const list = rows || [];
  const open = (t) => { setNavIds(list.map((x) => x.id)); setOpenId(t.id); };
  const kb = useListNav("tasks-ok", list.length, (i) => list[i] && open(list[i]), { enabled: keys });
  const onNavigate = (id) => { setOpenId(id); const i = list.findIndex((x) => x.id === id); if (i >= 0) kb.setSel(i); };
  const approve = async (t) => {
    if ((t.completion?.photos?.length || 0) > 0) { open(t); return; }
    setBusy(t.id);
    try {
      await api.post(`/tasks/${t.id}/approve`);
      setRows((r) => r.filter((x) => x.id !== t.id));
      toast.success(`Schváleno: ${t.title}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      open(t);
    } finally { setBusy(null); }
  };

  const total = rows ? rows.length : 0;
  return (
    <div className="pp-band-panel rounded-[18px] px-4 pt-3.5 pb-2.5 min-w-0" role="group" aria-label="Úkoly ke schválení" data-testid="task-approval-band">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2 min-w-0">
          <span className="truncate">Úkoly ke schválení</span>
          {total > 0 && <span className="pp-band-cnt" data-testid="task-approval-count">{total}</span>}
        </h3>
        <button type="button" onClick={() => nav("/ukoly")} className="pp-band-link shrink-0" data-testid="task-approval-open">Otevřít v Úkolech</button>
      </div>
      {rows === null ? (
        <div className="h-28 mt-3 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />
      ) : total === 0 ? (
        <div role="status" className="flex items-center gap-3 py-3" data-testid="task-approval-empty">
          <span className="w-9 h-9 rounded-full bg-success text-white grid place-items-center shrink-0"><Check size={18} strokeWidth={2.6} aria-hidden="true" /></span>
          <span className="text-[15px] font-bold">Vše schváleno</span>
        </div>
      ) : (<>
        <div className="flex items-center gap-3 mt-0.5">
          <p className="text-[13px] text-white/75 truncate min-w-0">{total} {word(total)} · u fotek si je nejdřív prohlédni</p>
          {keys && (   // nápověda kláves jen na počítači
            <span className="ml-auto shrink-0 hidden xl:flex items-center gap-1.5 text-[12px] text-white/75 whitespace-nowrap" data-testid="task-approval-keys">
              <Kbd className="!bg-white/15 !border-white/25 !text-white">↑</Kbd><Kbd className="!bg-white/15 !border-white/25 !text-white">↓</Kbd> vybrat
              <Kbd className="!bg-white/15 !border-white/25 !text-white">␣</Kbd> otevřít
            </span>
          )}
        </div>
        <div className="pp-band-scroll mt-2 max-h-[300px] overflow-y-auto -mx-2" data-testid="task-approval-list">
          {rows.map((t, i) => {
            const photos = t.completion?.photos?.length || 0;
            const who = (t.assignee_users || []).map((a) => a.name).filter(Boolean).join(", ");
            const when = t.completion?.at ? formatDate(t.completion.at) : "";
            return (
              // celý řádek otevře úkol; tlačítko s názvem je pro Tab a čtečku (klik probublá na řádek)
              <div key={t.id} {...kb.rowProps(i)} tabIndex={-1} onClick={() => { kb.setSel(i); open(t); }} data-testid={`task-approval-row-${t.id}`}
                className={`flex items-center gap-2.5 px-2 py-2 rounded-[8px] border-b border-white/[.12] cursor-pointer outline-none ${kb.sel === i ? "bg-white/[.12]" : ""}`}>
                <button type="button" className="flex-1 min-w-0 text-left" aria-label={`${t.title}${t.project_name ? `, ${t.project_name}` : ""} — otevřít úkol`}>
                  <span className="block text-sm font-bold truncate">{t.title}{t.project_name && <span className="font-medium text-white/75"> · {t.project_name}</span>}</span>
                  <span className="block mt-0.5 text-xs text-white/75 truncate">{[who, when && `hotovo ${when}`].filter(Boolean).join(" · ")}</span>
                </button>
                {photos > 0 && <span className="flex items-center gap-1 text-xs text-white/80 tabular-nums shrink-0" title={`${photos} fotek k prohlédnutí`}><ImageIcon size={13} aria-hidden="true" />{photos}</span>}
                <button type="button" onClick={(e) => { e.stopPropagation(); kb.setSel(i); approve(t); }} disabled={busy === t.id}
                  title={photos ? "Prohlédnout fotky a schválit" : "Schválit"} aria-label={`${photos ? "Prohlédnout fotky a schválit" : "Schválit"} — ${t.title}`}
                  data-testid={`task-approval-ok-${t.id}`}
                  className="w-[30px] h-[30px] rounded-[10px] bg-success text-white grid place-items-center shrink-0 disabled:opacity-60 hover:brightness-110">
                  {photos ? <ImageIcon size={15} aria-hidden="true" /> : <Check size={15} strokeWidth={2.6} aria-hidden="true" />}
                </button>
              </div>
            );
          })}
        </div>
      </>)}
      <TaskDetailModal taskId={openId} taskIds={navIds} onNavigate={onNavigate} open={!!openId}
        onOpenChange={(o) => { if (!o) setOpenId(null); }} onChanged={load} />
    </div>
  );
}
